import { useEffect, useState } from 'react';
import Step1 from './components/Step1';
import PrimaryButton from '../../components/atoms/Button';
import { CaretRight, CheckCircle } from '@phosphor-icons/react';
import Step2 from './components/Step2';
import Step3 from './components/Step3';
import { RootState } from '../../redux/store';
import { SATLinkStatus } from '../../@types/SATLinking';
import { useSelector } from 'react-redux';
import UnlinkedUser from '../home/organisms/UnlinkedUser';
import NewRegulationsBanner from '../../assets/img/banners/imss_new_regulations_desktop.png';
import NewRegulationsMobile from '../../assets/img/banners/imss_new_regulations_mobile.png';
import { useMediaQuery } from '@mui/material';
import Loading from '../../components/atoms/Loading';
import { useBlocker, useSearchParams } from 'react-router-dom';
import Modal from '../../components/molecules/Modal/Modal';
import Text from '../../components/atoms/Text';
import { useService } from '../../hooks/useService';
import useSuccessfulPurchaseEvent from '../../hooks/useSuccessfulPurchaseEvent';
import usePageView from '../../hooks/usePageView';
import { EVENT_TYPES } from '../../shared/constants/events';

const NewImsRegulations = () => {
	const { status, isLoading } = useSelector(
		(state: RootState) => state.satLinkStatus,
	);
	const userProfile = useSelector((state: RootState) => state.profile.user);

	const { getTaxPayer, getNssInfo, getUserRegimes, getAcquisitions } =
		useService();
	const { trackingEventSuccessfulPurchase } = useSuccessfulPurchaseEvent();
	const { trackPageView } = usePageView({
		sectionVisit: EVENT_TYPES.NSS_NEW_REGULATIONS,
	});

	const [step, setStep] = useState<number>(1);
	const [searchParams, setSearchParams] = useSearchParams();
	const [hasProcessedParams, setHasProcessedParams] = useState(false);

	const userIsLinked = Boolean(status?.status === SATLinkStatus.ACTIVE);
	const isMobile = useMediaQuery('(max-width: 768px)');

	let blocker = useBlocker(
		({ currentLocation, nextLocation }) => {
			const isLeavingPage = currentLocation.pathname !== nextLocation.pathname;
			const isInternalNavigation = nextLocation.pathname.includes('/new-imss-regulations');
			
			return isLeavingPage && !isInternalNavigation && userIsLinked;
		}
	);

	const showModalRecentAcquisition = 
		!hasProcessedParams && (
			Boolean(searchParams.get('show_modal_successful')) ||
			searchParams.get('redirect_status') === 'succeeded'
		);

	const handleCloseModalRecentAcquisition = () => {
		getNssInfo();
		setStep(2);
		const newParams = new URLSearchParams(searchParams);
		newParams.delete('show_modal_successful');
		newParams.delete('redirect_status');
		setSearchParams(newParams);
		setHasProcessedParams(true);
	};

	const handleExitPage = () => {
		if (blocker.proceed) {
			blocker.proceed();
		}
		setSearchParams(new URLSearchParams());
	};

	useEffect(() => {
		if (userProfile?.id) {
			getTaxPayer(userProfile?.id);
			getNssInfo();
			getUserRegimes();
			setTimeout(() => {
				getAcquisitions();
			}, 3000);
		}
	}, [userProfile?.id]);

	useEffect(() => {
		if (showModalRecentAcquisition) {
			trackingEventSuccessfulPurchase();
		}
	}, [showModalRecentAcquisition]);

	useEffect(() => {
		trackPageView();
		
		if (!hasProcessedParams) {
			const tab = searchParams.get('tab');
			const showModal = searchParams.get('show_modal_successful');
			const redirectStatus = searchParams.get('redirect_status');
			
			if (tab && !showModal && redirectStatus !== 'succeeded') {
				setStep(tab === 'imss' ? 2 : tab === 'fiscal' ? 3 : 1);
				const newParams = new URLSearchParams(searchParams);
				newParams.delete('tab');
				setSearchParams(newParams);
			}
			
			if (!showModal && redirectStatus !== 'succeeded') {
				setHasProcessedParams(true);
			}
		}
	}, [hasProcessedParams, searchParams]);

	if (isLoading) {
		return (
			<section className='py-4 px-2 md:py-6 md:px-12 flex flex-col gap-4 pb-10 text-sm max-w-[700px] mx-auto'>
				<Loading />
			</section>
		);
	}

	if (!userIsLinked) {
		return (
			<section className='px-2 py-6 max-w-[600px] mx-auto flex flex-col gap-4 fade-in-scale text-sm'>
				<img
					src={isMobile ? NewRegulationsMobile : NewRegulationsBanner}
					alt='New Regulations Banner'
					className='w-full h-auto border-2 shadow rounded-xl border-white'
				/>
				<UnlinkedUser
					showHeader={false}
					onSuccessLink={() => {
						window.location.reload();
					}}
					title='Vincula tu cuenta fiscal para poder ver el estado de tus declaraciones'
					description='Este proceso lo puedes hacer mediante tu e.firma o RFC.'
				/>
			</section>
		);
	}

	return (
		<section className='px-2 py-6 max-w-[700px] mx-auto flex flex-col gap-4 fade-in-scale text-sm'>
			<Modal
				open={blocker.state === 'blocked'}
				onClose={() => blocker.reset && blocker.reset()}
				sx='p-6 w-[90%] md:w-[500px]'
			>
				<div className='flex flex-col gap-2'>
					<Text
						size='body-1'
						weight='bold'
						sx='text-center mb-2 justify-center'
					>
						¿Tienes dudas de la nueva regulación?
					</Text>
					<Text
						size='body-3'
						weight='regular'
						sx='text-center mb-4'
					>
						Te damos asesoría y las resolvemos para que aproveches los
						beneficios y sigas trabajando con plataformas sin problema
					</Text>
					<PrimaryButton
						variant='primary'
						label='Recibir asesoría sin costo'
						onClick={() => {
							window.open('https://heru.trb.ai/wa/lQi8Pz', '_blank');
						}}
					/>
					<PrimaryButton
						variant='outline'
						label='Salir'
						onClick={handleExitPage}
						sx='justify-center mt-2'
						labelColor='blue'
						labelSize='caption'
					/>
				</div>
			</Modal>

			<Modal
				open={showModalRecentAcquisition}
				onClose={handleCloseModalRecentAcquisition}
				sx='p-6 w-[90%] md:w-[500px]'
			>
				<div className='flex flex-col gap-2 items-center'>
					<CheckCircle
						weight='fill'
						size={32}
						className='text-green-600'
					/>
					<Text
						size='body-1'
						weight='bold'
						sx='text-center mb-2 justify-center'
					>
						Compra exitosa
					</Text>
					<Text
						size='body-3'
						weight='regular'
						sx='text-center mb-4'
					>
						Gracias por tu compra, ahora puedes acceder a tu información
						completa de tu número de seguridad social y los comprobantes PDF
						oficiales.
					</Text>
					<PrimaryButton
						variant='primary'
						label='Continuar'
						onClick={handleCloseModalRecentAcquisition}
					/>
				</div>
			</Modal>

			<div className='flex gap-1 md:gap-2 items-center bg-gray-200/40 p-1 rounded-xl overflow-x-auto w-full tracking-tight'>
				<button
					className={`whitespace-nowrap text-sm h-8 px-1 w-full rounded-lg flex items-center gap-2 justify-center transition-all ${step === 1 ? 'bg-white shadow' : ''}`}
					onClick={() => {
						setStep(1);
						const newParams = new URLSearchParams(searchParams);
						newParams.delete('tab');
						setSearchParams(newParams);
					}}
				>
					Nueva regulación
				</button>
				<div className='h-[14px] min-w-[1px] bg-gray-300' />
				<button
					className={`whitespace-nowrap text-sm h-8 px-1 w-full rounded-lg flex items-center gap-2 justify-center transition-all ${step === 2 ? 'bg-white shadow' : ''}`}
					onClick={() => {
						setStep(2);
						const newParams = new URLSearchParams(searchParams);
						newParams.delete('tab');
						setSearchParams(newParams);
					}}
				>
					Seguridad social
				</button>
				<div className='h-[14px] min-w-[1px] bg-gray-300' />
				<button
					className={`whitespace-nowrap text-sm h-8 px-1 w-full rounded-lg flex items-center gap-2 justify-center transition-all ${step === 3 ? 'bg-white shadow' : ''}`}
					onClick={() => {
						setStep(3);
						const newParams = new URLSearchParams(searchParams);
						newParams.delete('tab');
						setSearchParams(newParams);
					}}
				>
					Perfil fiscal
				</button>
			</div>

			{step === 1 && <Step1 />}
			{step === 2 && <Step2 />}
			{step === 3 && <Step3 />}

			<div className='flex gap-2'>
				{step < 3 && (
					<PrimaryButton
						label={
							step === 1
								? 'Continuar: validar mi perfil IMSS'
								: 'Continuar: verificar mi perfil fiscal'
						}
						onClick={() => setStep(step + 1)}
						icon={
							<CaretRight
								weight='bold'
								className='min-w-5 min-h-5'
							/>
						}
						variant={'primary'}
					/>
				)}
			</div>
		</section>
	);
};

export default NewImsRegulations;
